import { Inject, Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Observable, firstValueFrom, tap } from 'rxjs';
import { ApiResponse } from './store.models';
import { CustomerAuthResponse, CustomerProfile, OtpRequestResponse } from './auth.models';
import { API_BASE, PLATFORM_COMPANY } from './ordering-api';
import { PushService } from './push.service';

const ACCESS_KEY = 'orderyaa.customer.access';
const REFRESH_KEY = 'orderyaa.customer.refresh';

/**
 * Phone + OTP customer auth. Signal-based session state; access/refresh tokens persisted to
 * localStorage (browser only). The token is attached to API calls by customerAuthInterceptor.
 */
@Injectable({ providedIn: 'root' })
export class CustomerAuthService {
  private readonly base = `${API_BASE}/api/customer-auth`;
  private readonly http = inject(HttpClient);
  private readonly push = inject(PushService);
  private readonly isBrowser: boolean;

  private readonly _accessToken = signal<string | null>(null);
  private readonly _refreshToken = signal<string | null>(null);
  private readonly _profile = signal<CustomerProfile | null>(null);

  readonly isAuthenticated = computed(() => !!this._accessToken());
  readonly profile = this._profile.asReadonly();

  /** Shared in-flight refresh so concurrent expired-token requests trigger ONE refresh call —
   *  otherwise each rotates the refresh token out from under the others and the session dies. */
  private _refreshPromise: Promise<boolean> | null = null;

  /** Bumped by clearSession(). A refresh captures it at the start and skips writing tokens if it
   *  changed by the time the call resolves — so a refresh that was in flight when the user logged
   *  out cannot silently RESURRECT the session (e.g. the logout push-token DELETE proactively
   *  refreshing an expired access token, then completing after teardown). */
  private _sessionEpoch = 0;

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);
    if (this.isBrowser) {
      this._accessToken.set(localStorage.getItem(ACCESS_KEY));
      this._refreshToken.set(localStorage.getItem(REFRESH_KEY));
      // Returning signed-in customer: silently refresh the push token (no re-prompt) on app open.
      if (this._accessToken()) void this.push.syncIfPermitted();
    }
  }

  get accessToken(): string | null {
    return this._accessToken();
  }

  /**
   * Silently exchange the refresh token for a fresh access token (~15-min access tokens; the
   * refresh endpoint rotates the refresh token). Returns true on success, false when there is no
   * refresh token or the server rejects it. Only tokens are updated — the loaded profile signal is
   * left intact (pages re-fetch it as needed). Concurrent callers share one in-flight request.
   */
  refreshAccessToken(): Promise<boolean> {
    if (this._refreshPromise) return this._refreshPromise;

    const refreshToken = this._refreshToken();
    if (!refreshToken) return Promise.resolve(false);

    const epoch = this._sessionEpoch; // capture: if the session is torn down before we resolve, don't resurrect it
    this._refreshPromise = firstValueFrom(
      this.http.post<ApiResponse<CustomerAuthResponse>>(`${this.base}/refresh`, { refreshToken }),
    )
      .then((res) => {
        if (epoch !== this._sessionEpoch) return false; // logged out meanwhile — discard this refresh
        if (res?.success && res.data?.accessToken) {
          this.setTokens(res.data.accessToken, res.data.refreshToken);
          return true;
        }
        return false;
      })
      .catch(() => false)
      .finally(() => { this._refreshPromise = null; });

    return this._refreshPromise;
  }

  // ── Token expiry (mirrors the partner/ERP auth services) ───────────────────

  private decodeToken(token: string): { exp?: number } {
    const parts = token.split('.');
    if (parts.length !== 3) throw new Error('Invalid JWT');
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(payload));
  }

  /** True when there is no token, or the current access token's exp has passed. */
  isTokenExpired(): boolean {
    const token = this._accessToken();
    if (!token) return true;
    try {
      const p = this.decodeToken(token);
      if (!p?.exp) return false; // no exp claim → treat as non-expiring
      return Date.now() >= p.exp * 1000;
    } catch {
      return true; // malformed token = expired
    }
  }

  /**
   * Resolves true when a usable session is in place — a live access token, or an expired one we
   * can silently refresh. False for a dead/absent session, so a stale token no longer renders as
   * "signed in" while every authenticated call quietly 401s (the empty-checkout bug).
   */
  async ensureAuthenticated(): Promise<boolean> {
    if (!this._accessToken()) return false;
    if (!this.isTokenExpired()) return true;
    return this.refreshAccessToken();
  }

  requestOtp(phone: string): Observable<ApiResponse<OtpRequestResponse>> {
    return this.http.post<ApiResponse<OtpRequestResponse>>(`${this.base}/otp/request`, { phone });
  }

  verifyOtp(phone: string, code: string): Observable<ApiResponse<CustomerAuthResponse>> {
    return this.http
      .post<ApiResponse<CustomerAuthResponse>>(`${this.base}/otp/verify`, { phone, code, platformCompanyId: PLATFORM_COMPANY.companyId || null })
      .pipe(tap((res) => res.success && res.data && this.setSession(res.data)));
  }

  /** Email + password signup — call after the sign-up OTP checked out, so the account starts verified. */
  register(body: { email: string; password: string; confirmPassword?: string; phone?: string | null; displayName?: string | null }): Observable<ApiResponse<CustomerAuthResponse>> {
    return this.http
      .post<ApiResponse<CustomerAuthResponse>>(`${this.base}/register`, { ...body, platformCompanyId: PLATFORM_COMPANY.companyId || null })
      .pipe(tap((res) => res.success && res.data && this.setSession(res.data)));
  }

  /** Sign-up email verification — send a one-time code (fails early if the email is taken). */
  requestSignupOtp(email: string): Observable<ApiResponse<OtpRequestResponse>> {
    return this.http.post<ApiResponse<OtpRequestResponse>>(`${this.base}/otp/signup/request`, { email });
  }

  /** Sign-up email verification — validate the emailed code only (no session; register() follows). */
  verifySignupOtp(email: string, code: string): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.base}/otp/signup/verify`, { email, code });
  }

  /** Email + password sign-in. */
  login(email: string, password: string): Observable<ApiResponse<CustomerAuthResponse>> {
    return this.http
      .post<ApiResponse<CustomerAuthResponse>>(`${this.base}/login`, { email, password })
      .pipe(tap((res) => res.success && res.data && this.setSession(res.data)));
  }

  /** Email OTP — send a code to an email address. */
  requestEmailOtp(email: string): Observable<ApiResponse<OtpRequestResponse>> {
    return this.http.post<ApiResponse<OtpRequestResponse>>(`${this.base}/otp/email/request`, { email });
  }

  /** Email OTP — verify the emailed code and start a session. */
  verifyEmailOtp(email: string, code: string): Observable<ApiResponse<CustomerAuthResponse>> {
    return this.http
      .post<ApiResponse<CustomerAuthResponse>>(`${this.base}/otp/email/verify`, { email, code, platformCompanyId: PLATFORM_COMPANY.companyId || null })
      .pipe(tap((res) => res.success && res.data && this.setSession(res.data)));
  }

  /** Forgot-password — email a reset code (always succeeds; doesn't reveal if the email exists). */
  requestPasswordReset(email: string): Observable<ApiResponse<OtpRequestResponse>> {
    return this.http.post<ApiResponse<OtpRequestResponse>>(`${this.base}/otp/forgot-password/request`, { email });
  }

  /** Forgot-password — check the reset code (without consuming it) before revealing the new-password step. */
  verifyResetCode(email: string, code: string): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.base}/otp/forgot-password/verify-code`, { email, code });
  }

  /** Forgot-password — verify the emailed code and set a new password. */
  resetPassword(email: string, code: string, newPassword: string): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.base}/otp/forgot-password/verify`, { email, code, newPassword });
  }

  /** Sign in with a Google ID token from Google Identity Services. */
  googleSignIn(idToken: string): Observable<ApiResponse<CustomerAuthResponse>> {
    return this.http
      .post<ApiResponse<CustomerAuthResponse>>(`${this.base}/social/google`, { idToken, platformCompanyId: PLATFORM_COMPANY.companyId || null })
      .pipe(tap((res) => res.success && res.data && this.setSession(res.data)));
  }

  /** Sign in with a Facebook user access token from the Facebook JS SDK. */
  facebookSignIn(accessToken: string): Observable<ApiResponse<CustomerAuthResponse>> {
    return this.http
      .post<ApiResponse<CustomerAuthResponse>>(`${this.base}/social/facebook`, { accessToken, platformCompanyId: PLATFORM_COMPANY.companyId || null })
      .pipe(tap((res) => res.success && res.data && this.setSession(res.data)));
  }

  loadProfile(): Observable<ApiResponse<CustomerProfile>> {
    return this.http
      .get<ApiResponse<CustomerProfile>>(`${this.base}/me`)
      .pipe(tap((res) => res.success && this._profile.set(res.data)));
  }

  logout(): void {
    const refresh = this._refreshToken();
    // Clear the device token server-side FIRST — the interceptor still has our live JWT here.
    void this.push.unregister();
    this.clearSession();
    if (this.isBrowser && refresh) {
      this.http.post(`${this.base}/logout`, { refreshToken: refresh }).subscribe({ error: () => {} });
    }
  }

  private setSession(d: CustomerAuthResponse): void {
    this.setTokens(d.accessToken, d.refreshToken);
    this._profile.set({
      id: '',
      phoneNumber: d.phoneNumber ?? null,
      phoneVerified: !!d.phoneNumber,
      displayName: d.displayName ?? null,
      email: d.email ?? null,
      emailVerified: !!d.email,
    });
    // Fresh sign-in: prompt for notification permission (if undecided) and register this device.
    void this.push.register();
  }

  /** Token-only update (sign-in AND silent refresh) — never touches the loaded profile. */
  private setTokens(accessToken: string, refreshToken?: string | null): void {
    this._accessToken.set(accessToken);
    if (refreshToken) this._refreshToken.set(refreshToken);
    if (this.isBrowser) {
      localStorage.setItem(ACCESS_KEY, accessToken);
      if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
    }
  }

  /** Local teardown (no server round-trip) — used when the session is already dead. */
  clearSession(): void {
    this._sessionEpoch++; // invalidate any in-flight refresh so it can't repopulate tokens after teardown
    this._accessToken.set(null);
    this._refreshToken.set(null);
    this._profile.set(null);
    if (this.isBrowser) {
      localStorage.removeItem(ACCESS_KEY);
      localStorage.removeItem(REFRESH_KEY);
      this.push.clearLocal(); // forget the cached push token so the next sign-in re-registers
    }
  }
}
